import type { GrafanaDashboard } from '@ipen-portal/ipen-entity-model';
import type { DynamicSubRoute } from '@ipen-portal/plugin-common-react';

import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { Icon } from '@iconify/react';
import {
  isIPenDomainEntity,
  isOrganisationDsoEntity,
  isProductEntity,
  isTribeEntity,
} from '@ipen-portal/ipen-entity-model';
import {
  isEntityWithGrafanaDashboards,
  normalizeEntityRef,
  notEmpty,
  or,
} from '@ipen-portal/plugin-common';
import { DynamicEntityRouteBlueprint } from '@ipen-portal/plugin-common-react';
import { useMemo } from 'react';
import useAsync from 'react-use/esm/useAsync';

import { FullSizeIFrame } from './GrafanaPage/components/FullSizeIFrame';
import { MonitoringHeaderButtons } from './GrafanaPage/components/MonitoringHeaderButtons';

const BASE_PATH = '/grafana-dashboard';

type DashboardResponse =
  | {
    dashboard: {
      title: string;
    };
    meta: {
      url: string;
    };
  }
  | {
    message: string;
    traceID: string;
  };

const loadDashboard = async (
  grafanaUrl: string,
  uid: null | string,
  token?: string,
) => {
  try {
    const response = await fetch(`${grafanaUrl}/api/dashboards/uid/${uid}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as DashboardResponse | undefined;
  } catch {
    return null;
  }
};

const initializeGrafanaSession = async (
  proxyBaseUrl: string,
  token?: string,
) => {
  const cookieUrl = `${proxyBaseUrl}/.backstage/auth/v1/cookie`;
  const response = await fetch(cookieUrl, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to initialize Grafana session: ${response.status} ${response.statusText}`,
    );
  }
};

const createRoute = async (
  grafanaUrl: string,
  dashboard: GrafanaDashboard,
  token?: string,
): Promise<DynamicSubRoute | null> => {
  const { icon, params, title, uid } = dashboard;
  const response = await loadDashboard(grafanaUrl, uid, token);
  if (!response || !('dashboard' in response)) {
    return null;
  }

  const queryParams = new URLSearchParams({ kiosk: 'true', theme: 'light' });
  for (const [key, value] of Object.entries(params ?? {})) {
    queryParams.set(`var-${key}`, value);
  }

  const iframeUrl = new URL(response.meta.url, grafanaUrl);
  queryParams.forEach((value, key) => {
    iframeUrl.searchParams.set(key, value);
  });

  return {
    children: (
      <FullSizeIFrame
        iframeTitle={response.dashboard.title}
        url={iframeUrl.href}
      />
    ),
    group: 'dashboards',
    icon: icon
      ? (
          <img alt="dashboard icon" height={24} src={icon} width={24} />
        )
      : (
          <Icon icon="ri:dashboard-3-line" />
        ),
    path: `${BASE_PATH}/${uid}`,
    title: title ?? response.dashboard.title,
    toolbarElements: MonitoringHeaderButtons,
  };
};

export const DynamicGrafana = DynamicEntityRouteBlueprint.make({
  name: 'dynamic-dashboards',
  params: {
    filter: or(
      isIPenDomainEntity,
      isOrganisationDsoEntity,
      isProductEntity,
      isTribeEntity,
    ),
    useDynamicSubRoutes: () => {
      const discoveryApi = useApi(discoveryApiRef);
      const identityApi = useApi(identityApiRef);

      const { entity } = useAsyncEntity();

      const { loading: loadingBackstageIdentity, value: profile } = useAsync(
        async () => await identityApi.getBackstageIdentity(),
        [identityApi],
      );

      const { loading: loadingDashboards, value: routes } =
        useAsync(async () => {
          if (!isEntityWithGrafanaDashboards(entity)) {
            return [];
          }

          const proxyBaseUrl = await discoveryApi.getBaseUrl('proxy');
          const grafanaUrl = `${proxyBaseUrl}/grafana`;
          const { token } = await identityApi.getCredentials();

          await initializeGrafanaSession(proxyBaseUrl, token);

          const privilegedDashboards = entity.spec.grafanaDashboards.filter(
            ({ privilegedAuthorities }) =>
              privilegedAuthorities?.some(authority =>
                profile?.ownershipEntityRefs.includes(
                  normalizeEntityRef(authority, 'Group'),
                ),
              ) ?? true,
          );

          return (
            await Promise.all(
              privilegedDashboards.map(dashboard =>
                createRoute(grafanaUrl, dashboard, token),
              ),
            )
          ).filter(notEmpty);
        }, [discoveryApi, entity, identityApi, profile]);

      const loading = loadingBackstageIdentity || loadingDashboards;
      return useMemo(
        () => ({
          dynamicRoutes: loading ? undefined : (routes ?? []),
          loading,
        }),
        [loading, routes],
      );
    },
  },
});
